import { useEffect, useRef, useState } from 'preact/hooks';
import { AdminDemosPanel, AdminOverviewPage, BotDetailPage, BotsListPage, PlanGate, useAccount } from './Dashboard';
import { choosingPlan } from './dashboardApi';
import { BillingPage, UsagePage } from './BillingUsage';
import { Pricing } from './Pricing';
import { Contact } from './Contact';
import { Privacy } from './Privacy';
import { Terms } from './Terms';
import { LeadsPage } from './Leads';
import { PROFILE_UPDATED_EVENT, ProfilePage } from './Profile';

/** The real snippet comes from the dashboard, with the bot's own id; this
 * one only shows how little there is to it. Built from the page's own
 * origin so it reads right on volgachat.com and in local dev alike. */
function exampleSnippet(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://volgachat.com';
  return `<script src="${origin}/v1/widget.js"\n        data-bot-id="your-bot-id"\n        defer></script>`;
}

interface CurrentUser {
  name: string;
  email: string;
  pictureUrl: string | null;
  role: 'user' | 'admin';
}

const GOOGLE_G_ICON =
  '<svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">' +
  '<path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>' +
  '<path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>' +
  '<path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z"/>' +
  '<path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.59-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>' +
  '</svg>';

// ============================================================================
// Router — one app, one router, one layout (see PHASE-8.1-NOTES.md). Only
// three real paths exist ("/", "/app", "/admin"), so this is deliberately
// the smallest thing that can be called a router, not a general-purpose
// one: current pathname in state, `navigate()` does a pushState + rerender,
// and a single document-level click listener upgrades any same-origin
// <a href> already in the markup into a client-side transition — nothing
// elsewhere had to change to "use the router." Server-side, GET /app and
// GET /admin still run requireAuth/requireRole on every real request (a
// fresh navigation, a reload, a bookmark) — this router only ever
// activates for a transition happening inside a page that already loaded,
// i.e. one the server already let through once. See customer.ts/admin.ts.
// The paths this SPA actually renders a view for — everything else that
// happens to be same-origin (/auth/google, /auth/logout, /hostile,
// /screenshots/*, /v1/*, /app/test/* which is a real iframe document) is a
// REAL page or a real server action, not a client-side view, and must
// always take a normal browser navigation. This is an allowlist, not a
// blocklist, deliberately: a new real page added later with no client-side
// view needs no update here to stay correctly un-intercepted.
//
// Phase 9 adds the /app/bots/<id> prefix, which is why this became a
// predicate rather than a Set.
/** The admin page's sections, one address each (AdminPage). */
const ADMIN_SECTIONS = [
  { path: '/admin', label: 'Sales demos' },
  { path: '/admin/bots', label: 'Customer bots' },
] as const;

function isAdminPath(pathname: string): boolean {
  return ADMIN_SECTIONS.some((section) => section.path === pathname);
}

function isAppOwnedPath(pathname: string): boolean {
  if (
    pathname === '/' ||
    isAdminPath(pathname) ||
    pathname === '/app' ||
    pathname === '/pricing' ||
    pathname === '/contact' ||
    pathname === '/privacy' ||
    pathname === '/terms'
  )
    return true;
  // /app/test/<id> is a real server-rendered page (the Test tab's iframe),
  // not a view — it must never be intercepted.
  if (pathname.startsWith('/app/test/')) return false;
  return pathname.startsWith('/app/');
}

// /admin and every /app path (the trial-starting screen included — reaching
// /app at all means signed in). Used to hide marketing chrome — the nav's
// sales links and CTA, the embedded demo widget — that has nothing to do
// with a signed-in customer's own dashboard.
function isDashboardPath(pathname: string): boolean {
  return isAdminPath(pathname) || pathname === '/app' || pathname.startsWith('/app/');
}

/** Returns the current path and a setter for the code that navigates on its
 * own (the signed-in home redirect in `Site`), so a programmatic
 * history change and the rendered view can't drift apart. */
function useRouter(): [string, (next: string) => void] {
  const [path, setPath] = useState(() => window.location.pathname);
  // The query too, though nothing routes on it: pages read their own
  // parameters from window.location (the Billing page's ?change=), and a
  // link that changes only the query — "Keep my current plan", from
  // /app/billing?change=… to /app/billing — left `path` the same, so
  // nothing re-rendered and the page stayed as it was.
  const [, setSearch] = useState(() => window.location.search);
  // And the hash: the signed-in home redirect reads it (a "/#faq" stays
  // on the marketing page), so a link that only drops it must re-render.
  const [, setHash] = useState(() => window.location.hash);

  useEffect(() => {
    function onPopState() {
      setPath(window.location.pathname);
      setSearch(window.location.search);
      setHash(window.location.hash);
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as Element).closest?.('a');
      if (!anchor || !anchor.href) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return; // external links (GitHub, mailto) navigate normally
      if (!isAppOwnedPath(url.pathname)) return; // /auth/*, /hostile, /v1/*, etc. — a real navigation, not a view change

      e.preventDefault();
      const samePath = url.pathname === window.location.pathname && url.search === window.location.search;
      if (samePath && !url.hash) {
        // The page the visitor is already on (the footer logo on "/"): no
        // new history entry — Back would seem to do nothing — just back to
        // the top, and without the hash the address may have carried,
        // which the signed-in home redirect reads.
        window.history.replaceState({}, '', url.pathname + url.search);
        setHash('');
        window.scrollTo(0, 0);
        return;
      }
      window.history.pushState({}, '', url.pathname + url.search + url.hash);
      setPath(url.pathname);
      setSearch(url.search);
      setHash(url.hash);
      if (url.hash) {
        // Give the new page's content a tick to mount before scrolling —
        // relevant when the hash link also changed the path (e.g. clicking
        // "Features" while on /app has to switch back to the home content
        // first).
        requestAnimationFrame(() => {
          document.getElementById(url.hash.slice(1))?.scrollIntoView();
        });
      } else if (!samePath) {
        window.scrollTo(0, 0);
      }
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return [path, setPath];
}

// ============================================================================
// Auth state — fetched once at the app root (not per-component) so the
// header, the dashboard, and the admin page all render from the exact same
// answer to "who is signed in right now," with one network request, not
// three.
type AuthState = CurrentUser | null | undefined; // undefined = still loading

// Whether the last answer from /auth/me said "signed in", remembered across
// loads. It exists only so that a signed-in visitor opening "/" doesn't
// watch the marketing hero paint and then vanish while /auth/me is still in
// flight — see the home redirect in `Site`. It is a hint, never an
// authority: /auth/me decides, and a stale hint costs one empty frame.
const SIGNED_IN_HINT_KEY = 'volgachat.signedIn';

function readSignedInHint(): boolean {
  try {
    return window.localStorage.getItem(SIGNED_IN_HINT_KEY) === '1';
  } catch {
    return false; // storage blocked (private mode, third-party cookie rules)
  }
}

function writeSignedInHint(signedIn: boolean): void {
  try {
    if (signedIn) window.localStorage.setItem(SIGNED_IN_HINT_KEY, '1');
    else window.localStorage.removeItem(SIGNED_IN_HINT_KEY);
  } catch {
    // The hint is an optimization; losing it just restores the old flash.
  }
}

function useCurrentUser(): AuthState {
  const [user, setUser] = useState<AuthState>(undefined);

  // Read again when the profile page saves a new name, so the account
  // menu doesn't keep showing the old one until the next page load.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const reread = () => setVersion((v) => v + 1);
    window.addEventListener(PROFILE_UPDATED_EVENT, reread);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, reread);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/auth/me')
      .then((res) => {
        // Signed out is a 200 with signedIn: false; anything else is the
        // check failing, which says nothing about the session either.
        if (!res.ok) throw new Error(`/auth/me answered ${res.status}`);
        return res.json();
      })
      .then((data: { signedIn: boolean; user?: CurrentUser }) => {
        const current = data.signedIn && data.user ? data.user : null;
        writeSignedInHint(current !== null);
        if (!cancelled) setUser(current);
      })
      .catch(() => {
        // A failed request says nothing about the session, so the hint is
        // left alone — only a real answer from /auth/me updates it. Nor
        // does a failed re-read after a profile save: the visitor is
        // still signed in, so keep what the first answer said.
        if (!cancelled && version === 0) setUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  return user;
}

function AuthControl({ user, dashboard }: { user: AuthState; dashboard: boolean }) {
  if (user === undefined) return null; // avoid a sign-in flash while /auth/me is in flight
  if (user === null) {
    // Both are the same Google sign-in link — every account signs in this
    // way, there's no separate signup, and either lands on the dashboard
    // (auth.ts's DEFAULT_RETURN_TO). They're captioned for the two people
    // who arrive here: a returning customer looks top-right for "Sign in",
    // as on nearly every other product's site, and shouldn't have to guess
    // that "Start free trial" is also their way back in. Below 640px the
    // bar has no room for both, and Sign in moves into the phone menu (Nav).
    return (
      <>
        <a class="site-signin-link" href="/auth/google" rel="nofollow" title="Sign in with your Google account">
          Sign in
        </a>
        <a class="site-btn site-btn-ghost site-signin" href="/auth/google" rel="nofollow">
          <span class="site-signin-icon" dangerouslySetInnerHTML={{ __html: GOOGLE_G_ICON }} />
          Start free trial
        </a>
      </>
    );
  }
  return <AccountControl user={user} dashboard={dashboard} />;
}

/** The signed-in corner: the Dashboard shortcut and the avatar's menu. Its
 * own component so only a signed-in visitor ever fetches their account. */
function AccountControl({ user, dashboard }: { user: CurrentUser; dashboard: boolean }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Shared with the dashboard (one request between them), and loaded with
  // the avatar rather than on the menu's first opening, so the menu never
  // changes under the visitor's pointer.
  const account = useAccount();
  // Not on a plan yet: every /app page is starting the free trial
  // (PlanGate), so for that moment the dashboard's pages and Upgrade have nothing to
  // offer — the menu is who's signed in and Sign out, as on most products'
  // setup screens. Hidden rather than disabled: greyed-out items with no
  // way to use them only invite clicks. While the account is still loading
  // (or couldn't be), the full menu shows; the server enforces every
  // limit regardless.
  const settingUp = !!account && choosingPlan(account);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  const initial = user.name.trim().charAt(0).toUpperCase() || '?';

  return (
    <>
      {/* Dashboard is the one thing a signed-in visitor comes back for, so it
          sits in the bar itself rather than behind the avatar — but not
          while already inside the dashboard, where it would just point at
          the page already open. */}
      {!dashboard && (
        <a class="site-btn site-btn-primary site-nav-dashboard" href="/app">
          Dashboard
        </a>
      )}
      <div class="site-account" ref={menuRef}>
        <button type="button" class="site-account-btn" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen} aria-haspopup="true">
          {user.pictureUrl ? (
            <img class="site-avatar" src={user.pictureUrl} alt="" referrerpolicy="no-referrer" />
          ) : (
            <span class="site-avatar site-avatar-fallback">{initial}</span>
          )}
        </button>
        {menuOpen && (
          // Closes on any item: the links are client-side transitions, which
          // the click-outside handler never sees. Only on an item, though —
          // a click on the name/email header is someone selecting it to copy.
          <div
            class="site-account-menu"
            role="menu"
            onClick={(e) => {
              if ((e.target as Element).closest('a, button')) setMenuOpen(false);
            }}
          >
            <div class="site-account-menu-header">
              <p class="site-account-menu-name">{user.name}</p>
              <p class="site-account-menu-email">{user.email}</p>
            </div>
            {!settingUp && (
              <>
                {/* Shown here too, for the narrow widths where the bar link is hidden. */}
                <a class="site-account-menu-dashboard" href="/app" role="menuitem">
                  Dashboard
                </a>
                <a href="/app/profile" role="menuitem">
                  Profile
                </a>
                {/* Money and quota, the two things a signed-in customer comes
                    looking for that aren't a bot. Above Upgrade deliberately:
                    someone checking what they already pay shouldn't have to
                    read past an invitation to pay more. */}
                <a href="/app/leads" role="menuitem">
                  Leads
                </a>
                <a href="/app/billing" role="menuitem">
                  Billing
                </a>
                <a href="/app/usage" role="menuitem">
                  Usage
                </a>
                {/* On every page once a plan is chosen — the one place a
                    signed-in visitor can reliably find their way to pricing.
                    (Before then, the free trial is still starting.) */}
                <a href="/pricing" role="menuitem">
                  Upgrade
                </a>
              </>
            )}
            {user.role === 'admin' && (
              <a href="/admin" role="menuitem">
                Admin
              </a>
            )}
            {/* Clear the hint on the way out so the home page it returns to
                renders immediately instead of waiting on /auth/me. */}
            <a href="/auth/logout" role="menuitem" onClick={() => writeSignedInHint(false)}>
              Sign out
            </a>
          </div>
        )}
      </div>
    </>
  );
}

// One shared header for every page — public or signed in. The logo and
// auth corner (AuthControl) are always there. "How it works"/"Features"/
// "Embed" are the pitch itself, so they disappear the moment someone
// signs in — not just inside the dashboard, but on any marketing page a
// signed-in visitor still reaches (e.g. /pricing from an Upgrade link):
// a customer who already has an account has nothing left to be pitched.
// "Pricing" stays for a signed-in visitor on marketing pages — it's how
// they upgrade — and only drops away on /admin and every /app path,
// same as before.
function Nav({ user, path }: { user: AuthState; path: string }) {
  const dashboard = isDashboardPath(path);
  const signedIn = !!user;
  // The phone menu. Below 760px the link row has no room beside the logo
  // and the sign-in button, so it folds behind a menu button instead of
  // disappearing — a visitor on a phone still has to be able to reach
  // Pricing and the FAQ. Closes itself on any navigation (the router
  // changes `path`, or a hash link scrolls the same page).
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    setMenuOpen(false);
  }, [path]);
  useEffect(() => {
    if (!menuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const links = (
    <>
      {!signedIn && (
        <>
          <a href="/#how-it-works">How it works</a>
          <a href="/#features">Features</a>
        </>
      )}
      <a href="/pricing">Pricing</a>
      {!signedIn && <a href="/#faq">FAQ</a>}
      <a href="/contact">Contact us</a>
    </>
  );

  return (
    <header class="site-nav">
      <div class="site-wrap site-nav-inner">
        {/* Home for a signed-in visitor is the dashboard, so the logo goes
            straight there rather than to "/" and out again via the
            redirect in `Site`. */}
        <a href={user ? '/app' : '/'} class="site-logo">
          <span class="site-logo-mark" aria-hidden="true" />
          VolgaChat
        </a>
        {!dashboard && (
          <nav class="site-nav-links" aria-label="Primary">
            {links}
          </nav>
        )}
        <div class="site-nav-right">
          <AuthControl user={user} dashboard={dashboard} />
          {!dashboard && (
            <button
              type="button"
              class="site-menu-btn"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="site-mobile-menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span class={`site-menu-icon${menuOpen ? ' site-menu-icon-open' : ''}`} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      {!dashboard && menuOpen && (
        <nav id="site-mobile-menu" class="site-mobile-menu" aria-label="Primary" onClick={() => setMenuOpen(false)}>
          <div class="site-wrap site-mobile-menu-inner">
            {links}
            {/* The bar's own Sign in link is hidden on phones; this is where
                a returning customer finds it there. */}
            {user === null && (
              <a class="site-mobile-menu-signin" href="/auth/google" rel="nofollow">
                <span class="site-signin-icon" dangerouslySetInnerHTML={{ __html: GOOGLE_G_ICON }} />
                Sign in
              </a>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}

/** Where every big button goes. Every account signs in with Google —
 * there's no separate signup — and a new account lands on the dashboard,
 * which starts its free trial. A visitor
 * who is already signed in (here via a /#section link from the dashboard)
 * is sent to their dashboard instead of back through sign-in. */
function startHref(user: AuthState): string {
  return user ? '/app' : '/auth/google';
}
function startLabel(user: AuthState): string {
  return user ? 'Go to your dashboard' : 'Start free trial';
}
/** Keeps crawlers from following a sign-in link: each hit starts an OAuth
 * flow and spends the sign-in rate limit every visitor shares (see
 * server/src/http/routes/auth.ts and server/public/robots.txt). */
function startRel(user: AuthState): string | undefined {
  return user ? undefined : 'nofollow';
}

// How long each beat of the hero conversation lasts before the next, in ms.
// The beats: 0 empty thread · 1 visitor's question · 2 assistant typing ·
// 3 answer · 4 quick replies · 5 "Talk to a person" pressed · 6 assistant
// typing · 7 lead form, the whole picture, held · 8 fading out, then over.
const MOCK_BEATS = [600, 900, 1300, 900, 1300, 600, 1300, 5000, 600];
const MOCK_FULL = 7;

/** Plays the hero conversation on a loop; just the finished conversation,
 * standing still, for a visitor who asked for less motion. */
function useMockBeat(): number {
  const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [beat, setBeat] = useState(() => (still() ? MOCK_FULL : 0));
  useEffect(() => {
    if (still()) return;
    const t = setTimeout(() => setBeat((b) => (b + 1) % MOCK_BEATS.length), MOCK_BEATS[beat]);
    return () => clearTimeout(t);
  }, [beat]);
  return beat;
}

/** The "assistant is typing" dots, shown in place of a reply about to land. */
function MockTyping() {
  return (
    <span class="site-mock-typing" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

/**
 * A conversation, drawn in HTML rather than shipped as a video: it plays
 * out the three things a buyer is paying for — an answer taken from their
 * own content (with the source named), quick replies, and a lead form —
 * in the space of one screen, on a loop, and stays crisp at any size.
 * Every message holds its space from the start (hidden, not absent), so
 * the mock never changes height and the hero beside it never jumps. The
 * live widget in the corner of the page is the real thing; this just makes
 * the pitch legible before anyone clicks it.
 */
function ChatMock() {
  const beat = useMockBeat();
  const msg = (shownAt: number, typingAt?: number) =>
    beat >= shownAt ? ' is-shown' : beat === typingAt ? ' is-typing' : '';
  return (
    <div
      class="site-mock"
      role="img"
      aria-label="Example conversation: a visitor asks whether the store ships to Canada. The assistant answers from the shipping policy, offers quick replies, and collects the visitor's name and email with a short form."
    >
      <div class="site-mock-header">
        <span class="site-mock-avatar">H</span>
        <div>
          <span class="site-mock-name">Harbor Trail Co.</span>
          <span class="site-mock-status">Online</span>
        </div>
      </div>
      <div class={`site-mock-body${beat === MOCK_BEATS.length - 1 ? ' is-resetting' : ''}`}>
        <div class={`site-mock-msg site-mock-user${msg(1)}`}>Do you ship to Canada, and how long does it take?</div>
        <div class={`site-mock-msg site-mock-bot${msg(3, 2)}`}>
          <MockTyping />
          Yes — we ship to Canada with tracked delivery, usually 5–8 business days. Orders over $75 ship free.
          <span class="site-mock-source">From: Shipping &amp; returns</span>
        </div>
        <div class={`site-mock-msg site-mock-chips${msg(4)}`}>
          <span>Return policy</span>
          <span>Track my order</span>
          <span class={beat >= 5 ? 'site-mock-chip-active' : undefined}>Talk to a person</span>
        </div>
        <div class={`site-mock-msg site-mock-bot${msg(7, 6)}`}>
          <MockTyping />
          Happy to arrange that — leave your details and the team will get back to you within one business day.
          <div class="site-mock-form">
            <span class="site-mock-field">Name</span>
            <span class="site-mock-field">Email</span>
            <span class="site-mock-send">Send</span>
          </div>
        </div>
      </div>
      <div class="site-mock-composer">
        <span>Ask a question…</span>
      </div>
    </div>
  );
}

function Hero({ user }: { user: AuthState }) {
  return (
    <section class="site-hero" id="top">
      <div class="site-wrap site-hero-grid">
        <div class="site-hero-copy">
          <span class="site-eyebrow">AI support agent for your website</span>
          <h1>Answer every visitor's question — instantly, from your own content.</h1>
          <p class="site-lead">
            VolgaChat reads your website and documents, then answers your customers in a chat window on your site —
            instantly, day and night. It only answers from your content, and says so when it doesn't know. Set up in
            minutes; installed with one line of code.
          </p>
          <div class="site-hero-ctas">
            <a href={startHref(user)} rel={startRel(user)} class="site-btn site-btn-primary site-btn-lg">
              {startLabel(user)}
            </a>
            <a href="/pricing" class="site-btn site-btn-ghost site-btn-lg">
              See pricing
            </a>
          </div>
          <ul class="site-trust-row" aria-label="Trial terms">
            <li>14-day free trial</li>
            <li>No credit card</li>
            <li>Cancel anytime</li>
          </ul>
          <p class="site-hero-hint">
            Try it now — the chat bubble in the corner of this page is VolgaChat, answering questions about itself.
          </p>
        </div>
        <ChatMock />
      </div>
    </section>
  );
}

const OUTCOMES = [
  {
    icon: '🕒',
    title: 'Support that never sleeps',
    body: 'Visitors get a real answer at 2 a.m. on a Sunday — not an auto-reply asking them to wait until Monday.',
  },
  {
    icon: '🎯',
    title: 'Fewer repeat questions',
    body: 'Opening hours, pricing, shipping, returns, how-tos: the questions you answer every day, handled before they reach your inbox.',
  },
  {
    icon: '📝',
    title: "Leads you'd otherwise lose",
    body: 'When a visitor wants a quote or a callback, the assistant collects their name and email right there in the chat.',
  },
];

function Outcomes() {
  return (
    <section class="site-section site-section-tight" aria-label="What VolgaChat does for your business">
      <div class="site-wrap">
        <div class="site-outcome-grid">
          {OUTCOMES.map((o) => (
            <div class="site-outcome" key={o.title}>
              <span class="site-outcome-icon" aria-hidden="true">
                {o.icon}
              </span>
              <div>
                <h3>{o.title}</h3>
                <p>{o.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Feature wall — every capability at a glance, as a cloud of chips. Only what
// the product actually does today: no "escalate to a human", no API, no
// compliance badges until those exist. A chip marked `pro` is gated by
// entitlements.ts (unansweredReports), so the badge must
// follow that file, not marketing taste.

type FeatureGroup = 'knowledge' | 'conversations' | 'brand' | 'dashboard' | 'trust';

const FEATURE_GROUPS: { id: FeatureGroup; label: string; blurb: string }[] = [
  {
    id: 'knowledge',
    label: 'Knowledge',
    blurb: 'Everything your assistant knows comes from you: your site, your files and the answers you save.',
  },
  {
    id: 'conversations',
    label: 'Conversations',
    blurb: 'More than a text box: one-tap questions, choices, forms and links that move visitors along.',
  },
  { id: 'brand', label: 'Look & install', blurb: 'Looks like part of your site, and goes in with one line.' },
  { id: 'dashboard', label: 'Dashboard', blurb: 'Read every conversation, test changes, and spot the gaps worth filling.' },
  { id: 'trust', label: 'Safety & privacy', blurb: 'Safe for your site, your budget and your visitors.' },
];

// Inner markup for a 24x24, stroke-drawn icon (Lucide shapes). Authored
// here, never user input, so injecting it is safe.
const FEATURE_ICONS = {
  globe:
    '<circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  network:
    '<rect x="16" y="16" width="6" height="6" rx="1"/><rect x="2" y="16" width="6" height="6" rx="1"/><rect x="9" y="2" width="6" height="6" rx="1"/><path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3"/><path d="M12 12V8"/>',
  file: '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>',
  pin: '<path d="M12 17v5"/><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"/>',
  refresh:
    '<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/>',
  badge:
    '<path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/>',
  bulb: '<path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/>',
  pointer:
    '<path d="m9 9 5 12 1.8-5.2L21 14Z"/><path d="M7.2 2.2 8 5.1"/><path d="m5.1 8-2.9-.8"/><path d="M14 4.1 12 6"/><path d="m6 12-1.9 2"/>',
  clipboard:
    '<rect width="8" height="4" x="8" y="2" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  clip: '<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>',
  palette:
    '<circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.65-.75 1.65-1.69 0-.44-.18-.84-.44-1.13-.29-.29-.44-.65-.44-1.13a1.64 1.64 0 0 1 1.67-1.67h2c3.05 0 5.55-2.5 5.55-5.55C21.97 6.01 17.46 2 12 2z"/>',
  video:
    '<path d="m16 13 5.22 3.48a.5.5 0 0 0 .78-.42V7.87a.5.5 0 0 0-.75-.43L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/>',
  move: '<path d="m5 9-3 3 3 3"/><path d="m9 5 3-3 3 3"/><path d="m15 19-3 3-3-3"/><path d="m19 9 3 3-3 3"/><path d="M2 12h20"/><path d="M12 2v20"/>',
  code: '<path d="m16 18 6-6-6-6"/><path d="m8 6-6 6 6 6"/>',
  messages:
    '<path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z"/><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"/>',
  question: '<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>',
  flask:
    '<path d="M10 2v7.53a2 2 0 0 1-.21.9L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.07-10.13A2 2 0 0 1 14 9.53V2"/><path d="M8.5 2h7"/><path d="M7 16h10"/>',
  bot: '<path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/>',
  sparkles:
    '<path d="M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.13-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.13a.5.5 0 0 1 .96 0l1.58 6.13a2 2 0 0 0 1.44 1.44l6.13 1.58a.5.5 0 0 1 0 .96l-6.13 1.58a2 2 0 0 0-1.44 1.44l-1.58 6.13a.5.5 0 0 1-.96 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/>',
  shield:
    '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>',
  gauge: '<path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/>',
  box: '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
  eyeOff:
    '<path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.53 13.53 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><path d="m2 2 20 20"/>',
  history: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/>',
};

interface FeatureChip {
  label: string;
  group: FeatureGroup;
  icon: keyof typeof FEATURE_ICONS;
  detail: string;
  pro?: boolean;
}

// Interleaved by group on purpose, so the wall reads as a mix of colours
// rather than five stripes; the group filter is what pulls them apart.
const FEATURE_CHIPS: FeatureChip[] = [
  { label: 'Website crawling', group: 'knowledge', icon: 'globe', detail: 'Paste your address and it reads your pages. You pick exactly which ones it may use.' },
  { label: 'Answers from your content', group: 'conversations', icon: 'badge', detail: "No invented prices or policies. When the answer isn't in your content, it says so." },
  { label: 'One-line install', group: 'brand', icon: 'code', detail: 'Paste one snippet before </body>. Works on WordPress, Shopify, Webflow, Wix, Squarespace and hand-built sites.' },
  { label: 'Conversation logs', group: 'dashboard', icon: 'messages', detail: 'Read every conversation in full, including the contact details visitors leave.' },
  { label: 'Verified domains only', group: 'trust', icon: 'shield', detail: "Runs only on sites you've proven you own, so nobody can lift your snippet and spend your quota." },
  { label: 'PDF & Word uploads', group: 'knowledge', icon: 'file', detail: 'Upload price lists, manuals and policies as PDF, Word, text or Markdown files.' },
  { label: 'Suggested questions', group: 'conversations', icon: 'bulb', detail: 'Up to six one-tap questions under the greeting, so visitors know where to start.' },
  { label: 'Custom logo & colours', group: 'brand', icon: 'palette', detail: 'Your brand colours, your own logo, or a different animated bubble from the gallery.' },
  { label: 'Unanswered questions', group: 'dashboard', icon: 'question', pro: true, detail: "See what your assistant couldn't answer, with similar questions grouped together, and close the gaps." },
  { label: 'Usage caps', group: 'trust', icon: 'gauge', detail: 'Rate limits and hard caps, so nobody can run up your bill.' },
  { label: 'Saved answers', group: 'knowledge', icon: 'pin', detail: 'Write the exact answer to an important question. It always wins, and takes effect the moment you save it.' },
  { label: 'Lead capture forms', group: 'conversations', icon: 'clipboard', detail: 'Collect a name, email or phone number before the chat starts, or when a visitor asks for a quote or a callback.' },
  { label: 'Animated chat bubble', group: 'brand', icon: 'video', detail: 'A friendly animated agent waves from the corner of your site. Silent, and never in the way.' },
  { label: 'Test before you launch', group: 'dashboard', icon: 'flask', detail: 'Chat with your assistant in the dashboard before anyone else sees it.' },
  { label: "Can't break your site", group: 'trust', icon: 'box', detail: 'The widget lives in its own isolated space, so your styles and scripts are never touched.' },
  { label: 'Sitemaps & JavaScript sites', group: 'knowledge', icon: 'network', detail: 'Finds pages through your sitemap, and reads sites built with JavaScript, not just plain HTML.' },
  { label: 'Quick replies', group: 'conversations', icon: 'pointer', detail: "Clickable choices when there are only a few options, so visitors don't have to type." },
  { label: 'Drag-anywhere bubble', group: 'brand', icon: 'move', detail: 'Visitors can move the bubble out of the way, and it remembers the spot on their next visit.' },
  { label: 'AI models included', group: 'dashboard', icon: 'sparkles', detail: 'The AI comes with every plan, the trial included. No API keys, no second bill.' },
  { label: 'No IP addresses stored', group: 'trust', icon: 'eyeOff', detail: "Your visitors' IP addresses are never stored." },
  { label: 'Weekly auto-refresh', group: 'knowledge', icon: 'refresh', detail: 'Your site is re-read every week on Professional and Business, and every month on Starter, so answers keep up with your pages. Rescan any time, the free trial included.' },
  { label: 'Link buttons', group: 'conversations', icon: 'link', detail: 'Sends visitors to your pricing, booking or docs pages, filled in automatically from your site.' },
  { label: 'Up to 3 chatbots', group: 'dashboard', icon: 'bot', detail: 'One per site or brand: 2 on Professional, 3 on Business, more on a custom plan.' },
  { label: 'Your retention rules', group: 'trust', icon: 'history', detail: 'Keep conversations from one day to a year, and delete any of them at any time.' },
];

function FeatureIcon({ name }: { name: keyof typeof FEATURE_ICONS }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: FEATURE_ICONS[name] }}
    />
  );
}

function FeatureWall() {
  const [group, setGroup] = useState<FeatureGroup | null>(null);
  const [active, setActive] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const wallRef = useRef<HTMLUListElement>(null);

  // The chips rise in once, the first time the wall scrolls into view. The
  // hidden starting state is only armed when the reveal can actually fire:
  // with no observer, or for a visitor who asked for less motion, the chips
  // are simply there.
  useEffect(() => {
    const wall = wallRef.current;
    if (!wall || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    wall.classList.add('is-armed');
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          wall.classList.add('is-in');
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(wall);
    return () => io.disconnect();
  }, []);

  // Feeds the soft spotlight that follows the cursor across the section.
  function onPointerMove(e: PointerEvent) {
    const el = sectionRef.current;
    if (!el || e.pointerType !== 'mouse') return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--fw-x', `${e.clientX - r.left}px`);
    el.style.setProperty('--fw-y', `${e.clientY - r.top}px`);
  }

  const chip = active === null ? null : FEATURE_CHIPS[active];
  const groupInfo = FEATURE_GROUPS.find((g) => g.id === group);

  return (
    <section class="site-section site-fw" ref={sectionRef} onPointerMove={onPointerMove} aria-labelledby="fw-title">
      <span class="site-fw-glow site-fw-glow-a" aria-hidden="true" />
      <span class="site-fw-glow site-fw-glow-b" aria-hidden="true" />
      <span class="site-fw-glow site-fw-glow-c" aria-hidden="true" />
      <div class="site-wrap">
        <p class="site-fw-eyebrow">
          <span class="site-fw-eyebrow-dot" aria-hidden="true" />
          What's included
        </p>
        <h2 id="fw-title">
          Everything you need to launch <span class="site-fw-gradient">your own AI assistant</span>
        </h2>
        <p class="site-section-lead">
          One subscription covers the AI, the hosting, the chat widget and the dashboard. Nothing to host, no API keys
          to manage.
        </p>

        <div class="site-fw-filters" role="group" aria-label="Show features by group">
          <button
            type="button"
            class="site-fw-filter"
            aria-pressed={group === null}
            onClick={() => setGroup(null)}
          >
            All <span class="site-fw-count">{FEATURE_CHIPS.length}</span>
          </button>
          {FEATURE_GROUPS.map((g) => (
            <button
              type="button"
              key={g.id}
              class="site-fw-filter"
              data-group={g.id}
              aria-pressed={group === g.id}
              onClick={() => {
                setGroup(group === g.id ? null : g.id);
                setActive(null);
              }}
            >
              <span class="site-fw-dot" aria-hidden="true" />
              {g.label}
              <span class="site-fw-count">{FEATURE_CHIPS.filter((c) => c.group === g.id).length}</span>
            </button>
          ))}
        </div>

        <ul class="site-fw-wall" ref={wallRef} aria-label="Features" onMouseLeave={() => setActive(null)}>
          {FEATURE_CHIPS.map((c, i) => {
            const state = group === null ? '' : c.group === group ? ' is-lit' : ' is-dim';
            return (
              <li class="site-fw-item" key={c.label} style={{ '--i': i }}>
                <button
                  type="button"
                  class={`site-fw-chip${state}${active === i ? ' is-active' : ''}`}
                  data-group={c.group}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                >
                  <span class="site-fw-icon">
                    <FeatureIcon name={c.icon} />
                  </span>
                  {c.label}
                  {c.pro && <span class="site-fw-pro">Pro</span>}
                </button>
              </li>
            );
          })}
        </ul>

        <div class="site-fw-caption" id="fw-caption" aria-live="polite" data-group={chip?.group ?? group ?? undefined}>
          {/* Keyed so each new caption mounts fresh and plays its fade-in. */}
          <div class="site-fw-caption-inner" key={chip ? chip.label : (group ?? 'none')}>
            {chip ? (
              <>
                <span class="site-fw-icon site-fw-icon-lg">
                  <FeatureIcon name={chip.icon} />
                </span>
                <p>
                  <strong>{chip.label}</strong>
                  {chip.detail}
                </p>
              </>
            ) : (
              <p class="site-fw-caption-hint">
                {groupInfo ? groupInfo.blurb : 'Point at, or tap, any feature to see what it does.'}
              </p>
            )}
          </div>
        </div>

        <p class="site-fw-note">
          <span class="site-fw-pro">Pro</span> Included on the Professional and Business plans.
        </p>
      </div>
    </section>
  );
}

const STEPS = [
  {
    n: '01',
    title: 'Add your content',
    body: 'Paste your website address and VolgaChat reads it — pages, sitemaps, even sites built with JavaScript. Upload PDFs, Word documents or text files too. You pick exactly which pages it may use.',
  },
  {
    n: '02',
    title: 'Make it yours',
    body: 'Name your assistant, write its greeting, choose your colours and logo, and pin exact answers to the questions that matter most. Test it in the dashboard before anyone else sees it.',
  },
  {
    n: '03',
    title: 'Paste one line',
    body: 'Copy the snippet from your dashboard into your site, just before the closing body tag. The chat bubble appears, answers start flowing, and on a paid plan your content is re-read regularly so it stays current.',
  },
];

function HowItWorks() {
  return (
    <section class="site-section site-section-alt" id="how-it-works">
      <div class="site-wrap">
        <h2>Up and running in an afternoon</h2>
        <p class="site-section-lead">
          Three steps. No servers to run, no AI accounts to set up, nothing to install on your site but one line.
        </p>
        <ol class="site-steps">
          {STEPS.map((step) => (
            <li class="site-step" key={step.n}>
              <span class="site-step-n">{step.n}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const FEATURES = [
  {
    icon: '📚',
    title: 'Answers only from your content',
    body: "No invented prices, policies or promises. If the answer isn't in your content, it says so and points the visitor to you — instead of making something up.",
  },
  {
    icon: '🌐',
    title: 'Learns your whole website',
    body: 'Paste your address and it reads your pages — sitemaps and JavaScript-built sites included. You choose which pages it may use, and on a paid plan it re-reads them regularly.',
  },
  {
    icon: '📄',
    title: 'Documents too',
    body: 'Upload price lists, manuals and policies — PDF, Word, text or Markdown — and they become part of what your assistant knows.',
  },
  {
    icon: '📌',
    title: 'Saved answers',
    body: 'Write the exact answer for your most important questions. A saved answer always wins over anything the assistant found on its own, and takes effect immediately.',
  },
  {
    icon: '🧩',
    title: 'Quick replies and lead forms',
    body: 'Offer clickable choices when there are only a few, and collect a name and email in the chat when a visitor wants a quote or a callback.',
  },
  {
    icon: '📊',
    title: 'Logs and unanswered-question reports',
    body: "Read every conversation, see which questions your assistant couldn't answer, and close the gaps with new content or a saved answer.",
  },
  {
    icon: '🎨',
    title: 'Your brand, your voice',
    body: "Your logo, your colours, your assistant's name and greeting — so it looks like part of your site, not something bolted on.",
  },
  {
    icon: '🛡️',
    title: 'Safe on your site',
    body: "Runs only on domains you've verified you own, can't interfere with your page's design or code, and has usage caps so nobody can run up your bill.",
  },
  {
    icon: '🔒',
    title: "Your visitors' privacy, built in",
    body: 'Visitor IP addresses are never stored. You decide how long conversations are kept — from a day to a year — and can delete any of them at any time.',
  },
];

function Features() {
  return (
    <section class="site-section" id="features">
      <div class="site-wrap">
        <h2>Everything a website assistant needs</h2>
        <p class="site-section-lead">Built for the business that owns the website, not just the developer who maintains it.</p>
        <div class="site-feature-grid">
          {FEATURES.map((f) => (
            <div class="site-feature-card" key={f.title}>
              <span class="site-feature-icon" aria-hidden="true">
                {f.icon}
              </span>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
        <p class="site-feature-note">Custom logo and colours are included on every plan, the free trial too.</p>
      </div>
    </section>
  );
}

function EmbedSection({ user }: { user: AuthState }) {
  const [copied, setCopied] = useState(false);
  const snippet = exampleSnippet();

  async function copySnippet() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (very old browser, insecure context) —
      // the snippet is still selectable text right below the button.
    }
  }

  return (
    <section class="site-section site-section-alt" id="embed">
      <div class="site-wrap site-embed-wrap">
        <div class="site-embed-copy">
          <h2>Installed in under a minute</h2>
          <p class="site-section-lead">
            Your dashboard gives you a snippet with your assistant's id. Paste it just before the closing{' '}
            <code>&lt;/body&gt;</code> tag on your site — that's the whole installation. It works anywhere you can add a
            script: WordPress, Shopify, Webflow, Wix, Squarespace or a hand-built site.
          </p>
          <ul class="site-embed-list">
            <li>No plugin or app to install, nothing to keep updated.</li>
            <li>Can't break your page — the widget lives in its own isolated area, so your styles and scripts are untouched.</li>
            <li>Loads after your content, and the chat window only loads when a visitor opens it.</li>
          </ul>
          <a href={startHref(user)} rel={startRel(user)} class="site-btn site-btn-primary site-embed-cta">
            {user ? 'Get your snippet' : 'Start free trial — get your snippet'}
          </a>
        </div>
        <div class="site-code-block">
          <div class="site-code-header">
            <span>index.html</span>
            <button type="button" class="site-copy-btn" onClick={copySnippet}>
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
          <pre>
            <code>{snippet}</code>
          </pre>
        </div>
      </div>
    </section>
  );
}

type FaqTopic = 'start' | 'assistant' | 'billing' | 'privacy';

const FAQ_TOPICS: { id: FaqTopic; label: string }[] = [
  { id: 'start', label: 'Getting started' },
  { id: 'assistant', label: 'Your assistant' },
  { id: 'billing', label: 'Plans & billing' },
  { id: 'privacy', label: 'Privacy & security' },
];

// Every answer here is checked against what the product actually does —
// limits from server/src/billing/entitlements.ts, verification from
// server/src/bots/verification.ts, retention from the dashboard routes.
// Change those and this list has to follow.
const FAQS: { topic: FaqTopic; q: string; a: string }[] = [
  {
    topic: 'start',
    q: 'Do I need a developer?',
    a: "No. You paste one snippet into your site, just before the closing body tag, or into your site builder's custom-code setting. That same snippet also proves you own the site, so there's no separate verification step. Everything else happens in the dashboard.",
  },
  {
    topic: 'start',
    q: 'Which website platforms does it work with?',
    a: 'Any site that lets you add a script: WordPress, Shopify, Webflow, Wix, Squarespace and hand-built sites alike. If your scripts load through a tag manager, verify the site with the small file the dashboard gives you instead.',
  },
  {
    topic: 'start',
    q: 'How does domain verification work?',
    a: 'Once the snippet is live on your home page over HTTPS, press Verify in the dashboard. A meta tag or a verification file work too. Each bot can run on up to 25 domains, and every subdomain (shop.example.com, say) is verified on its own.',
  },
  {
    topic: 'start',
    q: 'What can the assistant learn from?',
    a: 'Your website (it follows your sitemap and links, and reads sites built with JavaScript), PDF, Word (.docx), text and Markdown files up to 8 MB each, and saved answers you write yourself. You choose exactly which pages it may use.',
  },
  {
    topic: 'start',
    q: 'How does it stay up to date?',
    a: 'Your site is re-read automatically every week on Professional and Business, and every month on Starter (you can switch this off). On any plan, the free trial included, you can rescan it from the dashboard at any time. A saved answer takes effect the moment you save it.',
  },
  {
    topic: 'assistant',
    q: 'Will it make things up?',
    a: "It's built not to. The assistant answers only from the content you give it, and when the answer isn't there it says so and points visitors to you, rather than guessing at a price or a policy. Saved answers always win for the questions that matter most.",
  },
  {
    topic: 'assistant',
    q: 'Can visitors leave their contact details?',
    a: "Yes. Turn on the contact form to collect a name, email or phone number before the chat starts, or only when a visitor wants a follow-up. Everything they leave appears with the conversation in your dashboard.",
  },
  {
    topic: 'assistant',
    q: 'Is there live chat with a person?',
    a: "Not inside the widget — VolgaChat is an AI assistant. When it can't answer, it can offer your contact form, so you can pick the conversation up by email or phone.",
  },
  {
    topic: 'assistant',
    q: 'How much can I customise it?',
    a: 'Every plan lets you set the name, greeting, suggested questions and link buttons (filled in for you from your website), choose your own logo and brand colours, and pick an animated chat bubble from the gallery or upload your own.',
  },
  {
    topic: 'assistant',
    q: 'Will it slow down or break my site?',
    a: 'No. The script loads after your page does, and the chat runs in its own isolated space, so your styles and scripts are never touched. On phones the chat opens full-screen, and visitors can drag the bubble out of the way.',
  },
  {
    topic: 'assistant',
    q: 'Do I need my own AI account or API key?',
    a: 'No. The AI is included in every plan, the trial included. There is no other provider to sign up with, no second bill and no key to manage.',
  },
  {
    topic: 'billing',
    q: 'How does the free trial work?',
    a: "It's free for 14 days or 300 answered messages, whichever comes first, with no card needed. When it ends, nothing is deleted: your assistant pauses until you pick a plan, then carries on exactly where it left off.",
  },
  {
    topic: 'billing',
    q: 'What counts as a message?',
    a: "Each reply your assistant gives a visitor. Replies that fail don't count. Your allowance is shared by all the bots on your account and resets on the 1st of every month.",
  },
  {
    topic: 'billing',
    q: 'What happens if I run out of messages?',
    a: "Your assistant pauses and shows visitors a short notice until your allowance resets on the 1st, or until you move to a bigger plan. The Usage page in your dashboard shows how close you are, so it shouldn't catch you by surprise.",
  },
  {
    topic: 'billing',
    q: 'Monthly or yearly?',
    a: 'Every paid plan comes in both. Yearly is billed once a year and works out to one month free. Payments are handled securely by Dodo Payments.',
  },
  {
    topic: 'billing',
    q: 'How do I cancel or get an invoice?',
    a: 'Open Billing in your dashboard and choose Manage billing to cancel, update your card or download invoices. If you cancel, you keep your plan until the end of the period you paid for. Anything else: billing@volgachat.com.',
  },
  {
    topic: 'privacy',
    q: "What about my visitors' privacy?",
    a: "Your visitors' IP addresses are never stored. Conversations are kept only as long as you choose, from one day to a year, and you can delete any of them, or all of them, at any time. You can also show visitors a short notice that the chat is recorded.",
  },
  {
    topic: 'privacy',
    q: 'Can someone copy my snippet onto their own site?',
    a: "It won't work for them. The assistant only runs on domains you've verified you own, so nobody can lift your snippet and spend your messages. Rate limits and daily caps guard against abuse on your own site too.",
  },
];

function Faq() {
  const [topic, setTopic] = useState<FaqTopic | null>(null);
  const shown = topic ? FAQS.filter((f) => f.topic === topic) : FAQS;

  return (
    <section class="site-section site-section-alt" id="faq">
      <div class="site-wrap">
        <h2>Questions, answered</h2>
        <p class="site-section-lead">The things people ask before they start a trial.</p>
        <div class="site-faq-tabs" role="group" aria-label="Filter questions by topic">
          <button type="button" class="site-faq-tab" aria-pressed={topic === null} onClick={() => setTopic(null)}>
            All
          </button>
          {FAQ_TOPICS.map((t) => (
            <button
              type="button"
              key={t.id}
              class="site-faq-tab"
              aria-pressed={topic === t.id}
              onClick={() => setTopic(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        {/* Keyed by topic so a new filter remounts the list and replays its fade-in. */}
        <div class="site-faq" key={topic ?? 'all'}>
          {shown.map((item, i) => (
            <details key={item.q} style={{ '--i': i }}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
        <p class="site-faq-more">
          Still have a question? <a href="/contact">Contact us</a> and we'll point you to the right team.
        </p>
      </div>
    </section>
  );
}

function FinalCta({ user }: { user: AuthState }) {
  return (
    <section class="site-cta-band">
      <div class="site-wrap site-cta-inner">
        <h2>Give your visitors answers tonight.</h2>
        <p>Add your website, test your assistant, paste one line. Free for 14 days — no card, no commitment.</p>
        <div class="site-hero-ctas">
          <a href={startHref(user)} rel={startRel(user)} class="site-btn site-btn-light site-btn-lg">
            {startLabel(user)}
          </a>
          <a href="/pricing" class="site-btn site-btn-outline-light site-btn-lg">
            See pricing
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer({ user }: { user: AuthState }) {
  return (
    <footer class="site-footer">
      <div class="site-wrap site-footer-inner">
        <div>
          <a href="/" class="site-logo site-footer-logo">
            <span class="site-logo-mark" aria-hidden="true" />
            VolgaChat
          </a>
          <p class="site-footer-tagline">
            An AI assistant for your website that answers from your own content — set up in minutes, installed with one
            line.
          </p>
        </div>
        <nav class="site-footer-col" aria-label="Product">
          <h3>Product</h3>
          <a href="/#how-it-works">How it works</a>
          <a href="/#features">Features</a>
          <a href="/pricing">Pricing</a>
          <a href="/#faq">FAQ</a>
        </nav>
        <nav class="site-footer-col" aria-label="Company">
          <h3>Company</h3>
          <a href="/contact">Contact us</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          {user ? <a href="/app">Dashboard</a> : <a href="/auth/google" rel="nofollow">Sign in</a>}
        </nav>
      </div>
      <div class="site-wrap site-footer-copyright">© {new Date().getFullYear()} VolgaChat. All rights reserved.</div>
    </footer>
  );
}

function MarketingHome({ user }: { user: AuthState }) {
  return (
    <>
      <Hero user={user} />
      <Outcomes />
      <FeatureWall />
      <HowItWorks />
      <Features />
      <EmbedSection user={user} />
      <Faq />
      <FinalCta user={user} />
    </>
  );
}

// ============================================================================
// Signed-in pages — /app and /admin. Both are just another view this same
// app renders into the same <main>, under the same header/footer; nothing
// about them is a separate entry point, bundle, or shell. Real protection
// happens server-side before either of these ever gets a chance to render
// (server/src/http/routes/customer.ts, admin.ts) — the `user === null`
// checks below are a client-side safety net for a same-session transition
// that shouldn't be able to happen (see the top-of-file router comment),
// not the actual security boundary.

function ProfileCard({ user }: { user: CurrentUser }) {
  const initial = user.name.trim().charAt(0).toUpperCase() || '?';
  return (
    <div class="site-dashboard-profile">
      {user.pictureUrl ? (
        <img class="site-dashboard-avatar" src={user.pictureUrl} alt="" referrerpolicy="no-referrer" />
      ) : (
        <span class="site-dashboard-avatar site-avatar-fallback">{initial}</span>
      )}
      <div>
        <p class="site-dashboard-name">{user.name}</p>
        <p class="site-dashboard-email">{user.email}</p>
      </div>
    </div>
  );
}

/** /app and /app/bots/<id> — phase 9's real dashboard. The page components
 * live in ./Dashboard.tsx; this only decides which one the path means and
 * keeps the "are we actually signed in" safety net in one place. */
function DashboardRoute({ user, path }: { user: AuthState; path: string }) {
  useEffect(() => {
    // Safety net, not the security boundary — see this section's comment.
    if (user === null) window.location.href = path;
  }, [user, path]);

  if (!user) return null;

  const botMatch = /^\/app\/bots\/([^/]+)\/?$/.exec(path);
  const page = path.startsWith('/app/profile') ? (
    <ProfilePage />
  ) : path.startsWith('/app/leads') ? (
    <LeadsPage />
  ) : path.startsWith('/app/billing') ? (
    <BillingPage />
  ) : path.startsWith('/app/usage') ? (
    <UsagePage />
  ) : botMatch ? (
    <BotDetailPage botId={decodeURIComponent(botMatch[1])} />
  ) : (
    <BotsListPage />
  );
  return <PlanGate path={path}>{page}</PlanGate>;
}

function AdminPage({ user, path }: { user: AuthState; path: string }) {
  useEffect(() => {
    // Safety net, not the security boundary — see this section's comment.
    // requireRole('admin') already refused any real request that got here
    // without the role; this only covers a client-side transition into
    // /admin that somehow happened without it (shouldn't be reachable,
    // since AuthControl only ever shows the Admin link to an admin).
    if (user === null || (user && user.role !== 'admin')) window.location.href = '/admin';
  }, [user]);

  if (!user || user.role !== 'admin') return null;

  return (
    <section class="site-section site-dashboard">
      <div class="site-wrap">
        <ProfileCard user={user} />
        {/* One section at a time, picked from the rail (a strip on a
            phone) — the same layout as a bot's own page. Links, so each
            section has an address to reload or bookmark. */}
        <div class="db-shell">
          <nav class="db-side-nav" aria-label="Admin sections">
            {ADMIN_SECTIONS.map((section) => (
              <a
                key={section.path}
                href={section.path}
                class={`db-side-tab${path === section.path ? ' db-side-tab-active' : ''}`}
                aria-current={path === section.path ? 'page' : undefined}
              >
                {section.label}
              </a>
            ))}
          </nav>
          <div class="db-shell-body">
            {path === '/admin/bots' ? (
              <>
                <h1>Customer bots</h1>
                <p class="db-sub">Every bot across every account, read-only — who's using this, and who's about to cost money.</p>
                <AdminOverviewPage />
              </>
            ) : (
              <>
                <h1>Sales demos</h1>
                <p class="db-sub">
                  Build a chatbot from a prospect's public website, then send them the link to try it. Only you can see this — the demos live on
                  your own account.
                </p>
                <AdminDemosPanel />
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Site() {
  const [path, setPath] = useRouter();
  const user = useCurrentUser();

  // Once signed in, "/" is the dashboard — the marketing page is what you
  // were shown to get you here, not what you come back for. A hash is the
  // one exception: the header still links to same-page sections like
  // /#how-it-works, and those have to keep working from inside the
  // dashboard. /pricing is its own path, not a hash on "/", so it's
  // already unaffected by this redirect regardless.
  const homeWantsDashboard = path === '/' && !window.location.hash;

  useEffect(() => {
    if (!user || !homeWantsDashboard) return; // undefined = still loading, null = signed out
    // replace, not push: the redirect is not a place in the visitor's
    // history, and pushing it would make Back bounce straight back here.
    window.history.replaceState({}, '', '/app');
    setPath('/app');
  }, [user, homeWantsDashboard, setPath]);

  // index.html embeds this product's own demo widget on every page it
  // serves — which, since phase 8.1, includes the signed-in pages. On the
  // marketing site that's the point; on a customer's own dashboard it's
  // just confusing (and actively so on the Test tab, where their bot's
  // widget is the thing being looked at). Hide the host element there
  // rather than conditionally rendering a different shell.
  useEffect(() => {
    const host = document.getElementById('chat-widget-host');
    if (!host) return;
    host.style.display = isDashboardPath(path) ? 'none' : '';
  }, [path]);

  // Hold the marketing page back for the one frame before /auth/me answers,
  // but only for a visitor the hint says is signed in — an anonymous
  // visitor, the common case, still gets the landing page immediately.
  const awaitingHomeRedirect = user === undefined && homeWantsDashboard && readSignedInHint();

  const page =
    isAdminPath(path) ? (
      <AdminPage user={user} path={path} />
    ) : path.startsWith('/app') ? (
      <DashboardRoute user={user} path={path} />
    ) : path === '/pricing' ? (
      <Pricing />
    ) : path === '/contact' ? (
      <Contact />
    ) : path === '/privacy' ? (
      <Privacy />
    ) : path === '/terms' ? (
      <Terms />
    ) : awaitingHomeRedirect ? null : (
      <MarketingHome user={user} />
    );

  return (
    <>
      <Nav user={user} path={path} />
      <main>{page}</main>
      <Footer user={user} />
    </>
  );
}
